// main tools
import {
  split,
  Operation,
  ApolloLink,
  ApolloClient,
  InMemoryCache,
  createHttpLink
} from '@apollo/client'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { setContext } from '@apollo/client/link/context'
import { getSession } from 'next-auth/react'

// utils
import { microServicesEnum } from '@dr-keyword-commons/enums'
import { microservicesUrisEnum } from './urls'
import { createClient } from 'graphql-ws'

/**
 * configure apollo for
 * create a new apollo client
 */
export const createApolloClient = (accessToken: string | null = null) => {
  const backendUri = microservicesUrisEnum.BACKEND


  // Important: if `uri` is not set, Apollo defaults to `"/graphql"` on the current origin.
  // We set it explicitly to avoid accidental calls to `https://<frontend-domain>/graphql`.
  const httpLink = createHttpLink({
    uri: backendUri,
    fetch
  })

  const wsUrl = process.env.NEXT_PUBLIC_BASE_WS_API_URL

  const isValidUrl = (value: unknown): value is string => {
    if (!value || typeof value !== 'string') return false
    const trimmed = value.trim()
    if (!trimmed) return false
    try {
      // Soporta http(s)/ws(s) y también rechaza `''` o valores rotos.
      const parsed = new URL(trimmed)
      return Boolean(parsed.protocol)
    } catch {
      return false
    }
  }

  const initializeWs = (url: string) =>
    createClient({
      url,
      connectionParams: async () => {
        const session =
          typeof window !== 'undefined' ? await getSession() : null
        return {
          authorization: `Bearer ${session ? session.token : ''}`
        }
      }
    })

  let wsLink: GraphQLWsLink | null = null
  if (typeof window !== 'undefined' && isValidUrl(wsUrl)) {
    try {
      wsLink = new GraphQLWsLink(initializeWs(wsUrl))
    } catch {
      wsLink = null
    }
  }

  // mapper of the micro service selected
  const uriMapper = new ApolloLink((operation, forward) => {
    const { ms } = operation.getContext()
    const mappedUri =
      microservicesUrisEnum[ms as keyof typeof microservicesUrisEnum]
    operation.setContext({
      // Guard: si el `ms` no matchea nuestras keys (por diferencias entre enums),
      // evitamos que Apollo caiga al default `/graphql` del dominio.
      uri: mappedUri ?? microservicesUrisEnum.BACKEND
    })

    return forward(operation)
  })

  // set the headers for the micro service selected
  const apolloHeaders = setContext(async (_, { ms, headers }) => {
    let token = null
    if (typeof window !== 'undefined') {
      if (ms === microServicesEnum.BACKEND) {
        const session = await getSession()
        token = session?.token
      }
    } else token = accessToken

    return {
      headers: { ...headers, authorization: token ? `Bearer ${token}` : '' }
    }
  })

  // concat the links
  const link = uriMapper.concat(apolloHeaders.concat(httpLink))

  const splitCallback = ({ query }: Operation) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  }

  const splitLink =
    typeof window !== 'undefined' && wsLink
      ? split(splitCallback, wsLink, link)
      : link

  // create the apollo client
  const client = new ApolloClient({
    link: splitLink,
    ssrMode: typeof window === 'undefined',
    cache: new InMemoryCache({ resultCaching: false, addTypename: false }),
    defaultOptions: {
      query: { fetchPolicy: 'no-cache' },
      watchQuery: { fetchPolicy: 'no-cache' }
    }
  })

  return client
}
